import { Editor, EditorChange } from 'codemirror';
import produce, { applyPatches, Patch } from 'immer';
import React from 'react';
import { Controlled as CodeMirror } from 'react-codemirror2';
import { Icon } from 'semantic-ui-react';

import { styled } from '@modules/common/components/ThemeProvider/utils';
import LinkButton from '../../../common/LinkButton';

require('codemirror/addon/hint/javascript-hint.js');
require('codemirror/addon/hint/show-hint.js');
require('codemirror/addon/lint/javascript-lint.js');
require('codemirror/addon/lint/json-lint.js');
require('codemirror/addon/lint/lint.css');
require('codemirror/addon/lint/lint.js');
require('codemirror/lib/codemirror.css');
require('codemirror/mode/javascript/javascript');

export type CodeEditorProps = {
  isCodeValid?: (code: string) => boolean;
  mode?: 'javascript' | 'xml';
  lineNumbers?: boolean;
  readOnly?: boolean;
  onCodeChange: (code: string, isValid: boolean) => any;
  code?: string;
  style?: React.CSSProperties;
  toggleHelp?: () => void;
  helpDisplayed?: boolean;
  title?: string;
  showHelp?: boolean;
};

const initialState = Object.freeze({ historyIndex: 0, helpDisplayed: false });

type State = typeof initialState;

class CodeEditorContainer extends React.PureComponent<CodeEditorProps, State> {
  changes: Patch[] = [];
  inverseChanges: Patch[] = [];

  static defaultProps = {
    code: '',
    mode: 'javascript',
    lineNumbers: true,
    helpDisplayed: false,
    showHelp: false,
    title: 'Custom script editor',
    readOnly: false,
  };

  readonly state: State = initialState;

  handleCodeChange = (_1: Editor, _2: EditorChange, value: string) => {
    const codeState = { code: this.props.code };
    this.setState(state => {
      produce(
        codeState,
        draft => {
          draft.code = value;
        },
        (patches, inversePatches) => {
          this.changes.push(patches[0]);
          this.inverseChanges.push(inversePatches[0]);
        },
      );
      return { historyIndex: state.historyIndex + 1 };
    }, this.handleCodeChangeValid(value));
  };

  undoCodeChange = () => {
    if (this.canUndoCodeChange()) {
      const undoPatch = this.inverseChanges[this.state.historyIndex - 1];
      if (undoPatch) {
        this.applyPatch(undoPatch, -1);
      }
    }
  };

  redoCodeChange = () => {
    if (this.canRedoCodeChange()) {
      const redoPatch = this.changes[this.state.historyIndex];
      if (redoPatch) {
        this.applyPatch(redoPatch, 1);
      }
    }
  };

  applyPatch = (codePatch: Patch, historyChange: number) => {
    const codeState = { code: this.props.code };
    const updatedState = applyPatches(codeState, [codePatch]);
    this.setState(
      state => ({
        historyIndex: state.historyIndex + historyChange,
      }),
      this.handleCodeChangeValid(updatedState.code!),
    );
  };

  handleCodeChangeValid = (code: string) => {
    const isValid = this.props.isCodeValid ? this.props.isCodeValid(code) : true;
    return this.props.onCodeChange(code, isValid);
  };

  canRedoCodeChange = () => {
    return this.changes.length > 0 && this.state.historyIndex < this.changes.length;
  };
  canUndoCodeChange = () => {
    return this.inverseChanges.length > 0 && this.state.historyIndex > 0;
  };

  render() {
    const { mode, lineNumbers, code, style, toggleHelp, helpDisplayed, readOnly } = this.props;
    const undoClassname = !this.canUndoCodeChange() ? '' : 'active';
    const redoClassname = !this.canRedoCodeChange() ? '' : 'active';
    const helpIcon = helpDisplayed ? 'remove' : 'help';

    const options = {
      mode,
      lineNumbers,
      lint: {
        esversion: 6,
      },
      readOnly,
    } as any;

    return (
      <CodeEditorStyle style={style}>
        <div className="code-editor-header">
          <div className="code-editor-title">{this.props.title}</div>

          <div className="history-edit">
            {toggleHelp && (
              <LinkButton title="Show help" className="active" onClick={toggleHelp}>
                <Icon name={helpIcon} />
              </LinkButton>
            )}

            {!readOnly && (
              <LinkButton
                title="Undo change"
                onClick={this.undoCodeChange}
                className={undoClassname}
              >
                <Icon name="reply" />
              </LinkButton>
            )}
            {!readOnly && (
              <LinkButton
                title="Redo change"
                onClick={this.redoCodeChange}
                className={redoClassname}
              >
                <Icon name="share" />
              </LinkButton>
            )}
          </div>
        </div>

        <CodeMirror
          value={code!}
          options={options}
          onBeforeChange={this.handleCodeChange}
          className="code-mirror__root"
        />
      </CodeEditorStyle>
    );
  }
}

type CodeEditorStyleProps = {
  style?: React.CSSProperties;
};

const CodeEditorStyle = styled('div')<CodeEditorStyleProps>`
  position: relative;
  height: ${props => (props.style && props.style.height ? props.style.height : '300px')};

  .code-editor-header {
    background: #f2f2f2;
    padding: 10px;
    height: 45px;
    border-bottom: 1px solid #ccc;
    font-weight: bold;
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    .code-editor-title {
      margin-top: 3px;
    }

    .history-edit {
      z-index: 10;
      box-shadow: 0 0 3px #aaa;
      padding: 2px 0 2px 4px;
    }

    .history-edit > button {
      text-decoration: none;
      opacity: 0.6;
      color: gray;
    }

    .history-edit > button.active {
      color: #4183c4 !important;
    }

    .history-edit > button.active:hover {
      opacity: 1;
      cursor: pointer;
    }
  }

  .code-mirror__root {
    height: calc(100% - 45px);
    display: flex;

    .cm-s-default {
      flex-grow: 1;
      height: 100%;
    }
  }
`;

export default CodeEditorContainer;
